import React, { ReactElement, useState } from 'react';
import { Text } from '../../components';

export default function FeatureGrid(): ReactElement {
  const [seeingStars, setSeeingStars] = useState<boolean>(false);

  const handleLogoClick = () => {
    if (!seeingStars) {
      // Uses a modified version of the Starfield-effect by Ashik Paul. Original Repo: https://github.com/Ashikpaul/Starfield-effect
      const canvas = document.getElementById(
        'starfield-canvas'
      ) as HTMLCanvasElement;
      const context = canvas.getContext('2d');
      if (!context) return;

      // Bound to a non-nullable local because the Star methods and draw() below
      // close over it — narrowing from the guard above does not reach into them.
      const c: CanvasRenderingContext2D = context;

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      class Star {
        x: number;
        y: number;
        z: number;
        px: number;
        py: number;

        constructor() {
          //initializing
          this.x = Math.random() * canvas.width - canvas.width / 2; //random x
          this.y = Math.random() * canvas.height - canvas.height / 2; //random y
          this.px = this.x;
          this.py = this.y;
          this.z = Math.random() * 4; //random z
        }

        update() {
          //stores previous x, y and z and generates new coordinates
          this.px = this.x;
          this.py = this.y;
          this.z += speed;
          this.x += this.x * (speed * 0.2) * this.z;
          this.y += this.y * (speed * 0.2) * this.z;
          if (
            this.x > canvas.width / 2 + 50 ||
            this.x < -canvas.width / 2 - 50 ||
            this.y > canvas.height / 2 + 50 ||
            this.y < -canvas.height / 2 - 50
          ) {
            this.x = Math.random() * canvas.width - canvas.width / 2;
            this.y = Math.random() * canvas.height - canvas.height / 2;
            this.px = this.x;
            this.py = this.y;
            this.z = 0;
          }
        }

        //draws line from x,y to px,py
        show() {
          c.lineWidth = this.z;
          c.beginPath();
          c.moveTo(this.x, this.y);
          c.lineTo(this.px, this.py);
          c.stroke();
        }
      }

      let speed = 0.04;
      const stars: Star[] = [];

      for (let i = 0; i < 200; i++) stars.push(new Star());

      c.fillStyle = 'rgba(0, 0, 0, 0.1)';
      c.strokeStyle = 'rgb(255, 255, 255, 0.8)';

      c.translate(canvas.width / 2, canvas.height / 2);

      function draw() {
        //create rectangle
        c.fillRect(
          -canvas.width / 2,
          -canvas.height / 2,
          canvas.width,
          canvas.height
        );
        for (let s of stars) {
          s.update();
          s.show();
        }
        //infinte call to draw
        requestAnimationFrame(draw);
      }

      draw();

      const bg = document.getElementById('worksBg');
      bg?.classList.add('bg-neutral-black');
      const img = document.getElementById('worksImg');
      img?.classList.add('scale-[0.7]', 'opacity-0');
      const text = document.getElementById('worksText');
      text?.classList.add('opacity-0');
      const svg = document.getElementById('worksSvg');
      svg?.classList.add(
        'rounded-full!',
        'shadow-[0px_0px_70px_8px_var(--color-base-purple-500)]!',
        // border-radius and box-shadow have different durations and delays,
        // which no single transition utility expresses.
        '[transition:border-radius_0.35s_0.25s_ease,box-shadow_0.5s_0.5s_ease]'
      );

      canvas.style.opacity = '1';

      setSeeingStars(true);
    }
  };

  return (
    <section className="mt-16 mb-28 flex justify-center p-4">
      <div className="relative w-240 max-w-240">
        <img
          className="absolute -top-10.5 -left-113.75 w-2xl"
          alt=""
          role="presentation"
          src="/homepage-blob-1.png"
        />
        <img
          className="absolute -right-73.5 -bottom-32.5 w-154"
          alt=""
          role="presentation"
          src={`/homepage-blob-2.png`}
        />
        <div className="grid grid-cols-2 gap-5 [grid-template-areas:'a_b'_'a_c'_'d_d'_'e_f'_'g_f'_'h_i'_'h_j'_'k_j'_'l_l'] max-nav:grid-cols-1 max-nav:[grid-template-areas:none] max-nav:[&_img:not(.feature-grid-img-full)]:max-w-full! max-nav:[&>div]:[grid-area:initial]!">
          <div
            className="relative flex flex-col items-center justify-center rounded-[40px] bg-base-teal pr-8 pl-8"
            style={{ gridArea: 'a' }}>
            <img
              className="mt-8 max-w-90 rounded-lg"
              alt="The Stark Contrast Checker showing white and red colors failing AAA WCAG standards and showing two rows of passing suggested colors at the bottom."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761248929/marketing/features/contrast__052023_qeumiv.webp"
              style={{
                willChange: 'filter',
                filter: 'drop-shadow(0px 8px 60px rgba(0, 0, 0, 0.16))',
              }}
            />
            <Text level="mono" color="blue" className="mt-8 mb-8 text-center">
              Contrast Checker + Color Suggestions
            </Text>
          </div>
          <div
            className="flex flex-col items-center rounded-[40px] bg-base-yellow pr-8 pl-8"
            style={{
              gridArea: 'b',
            }}>
            <img
              className="mt-8 max-w-94.5"
              alt="Two bounding boxes connected with a line and annotated with the numbers 1 and 2 showing the intended order of focus."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761248935/marketing/features/focus-order_mnsbpn.png"
            />

            <Text level="mono" color="blue" className="mt-8 mb-8 text-center">
              Focus Order
            </Text>
          </div>

          <div
            className="flex flex-col items-center overflow-hidden rounded-[40px] bg-base-pink pr-8 pl-8"
            style={{ gridArea: 'c' }}>
            <Text level="mono" color="blue" className="mt-8 mb-8 text-center">
              Landmarks
            </Text>
            <img
              className="max-w-97.5 translate-y-px"
              alt="A website about plants annotated with the Stark Landmarks feature highlighting the header navigation, main content of the site, and a section of bestsellers."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761248978/marketing/features/landmarks_xamlzx.png"
              style={{
                willChange: 'filter',
                filter: 'drop-shadow(0px 8px 60px rgba(0, 0, 0, 0.16))',
              }}
            />
          </div>

          <div
            className="relative flex h-94 items-center justify-center overflow-hidden rounded-[40px] bg-base-purple-300"
            style={{
              gridArea: 'd',
            }}>
            <Text
              level="mono"
              color="blue"
              className="absolute bottom-8 text-center">
              Vision Simulator + Generator
            </Text>
            <img
              className="feature-grid-img-full mt-12 w-274.25 max-w-274.25"
              alt="The same photo of a living room covered in plants with a mirror on the wall and an orange chair to the right is shown in the various colorblind simulations that Stark offers including Protanopia, Tritanopia, Achromatopsia and more."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761249013/marketing/features/vision-simulator_u4ug2i.webp"
            />
          </div>

          <div
            className="relative flex flex-col items-center justify-between overflow-hidden rounded-[40px] bg-base-yellow pr-8 pl-8"
            style={{
              gridArea: 'e',
            }}>
            <img
              className="max-w-85.5"
              alt="Stark's Touch Target tool showing a layer not meeting Web Mobile, iOS, or Android standards for touch target height."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761249008/marketing/features/touch-targets_cp6rcn.png"
              style={{
                willChange: 'filter',
                filter: 'drop-shadow(0px 8px 60px rgba(0, 0, 0, 0.16))',
              }}
            />
            <Text level="mono" color="blue" className="mt-8 mb-8 text-center">
              Touch Targets
            </Text>
          </div>

          <div
            id="worksBg"
            className="relative flex flex-col items-center justify-center rounded-[40px] bg-base-teal p-4 transition-colors"
            style={{
              gridArea: 'f',
            }}>
            <img
              id="worksImg"
              className="z-10 mt-12 max-w-94 transition-all"
              alt="Stark's logo surrounded by design tools (Figma, Sketch) and web browsers (Chrome, Edge, and Brave)."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761248994/marketing/features/platform-grid_co1ov8.webp"
            />
            <div
              id="worksSvg"
              onClick={handleLogoClick}
              className="absolute z-10 h-40 w-40 -translate-y-4 cursor-pointer overflow-hidden rounded-[36px]"
              style={{
                boxShadow: '0px 2.13592px 4.27184px rgba(0, 0, 0, 0.28)',
              }}>
              <svg
                width="160"
                height="160"
                viewBox="0 0 160 160"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <title>Stark logo</title>
                <rect width="160" height="160" fill="white" />
                <rect
                  width="160"
                  height="160"
                  fill="url(#paint0_linear_2312_20415)"
                />
                <g filter="url(#filter0_di_2312_20415)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M77.5151 124.577V111.146C86.7076 111.945 95.7826 108.635 102.301 102.104C112.684 91.7201 114.443 75.511 106.529 63.1417L63.3346 106.332L53.7049 115.961C51.9015 114.628 50.2001 113.163 48.615 111.577C31.1745 94.1382 31.1745 65.8629 48.615 48.4227C57.6416 39.3769 70.1185 34.6425 82.8743 35.4231V48.8545C73.6818 48.0547 64.6068 51.3651 58.0889 57.8959C47.7056 68.2799 45.9466 84.489 53.8606 96.8583L97.0548 53.6687L106.685 44.0399C108.488 45.3723 110.19 46.8377 111.775 48.4236C129.215 65.8626 129.215 94.1378 111.775 111.578C102.748 120.624 90.2709 125.358 77.5151 124.577Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <filter
                    id="filter0_di_2312_20415"
                    x="30.0978"
                    y="32.8156"
                    width="100.194"
                    height="100.194"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="2.91262" />
                    <feGaussianBlur stdDeviation="2.71845" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_2312_20415"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect1_dropShadow_2312_20415"
                      result="shape"
                    />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="0.38835" />
                    <feComposite
                      in2="hardAlpha"
                      operator="arithmetic"
                      k2="-1"
                      k3="1"
                    />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="shape"
                      result="effect2_innerShadow_2312_20415"
                    />
                  </filter>
                  <linearGradient
                    id="paint0_linear_2312_20415"
                    x1="80"
                    y1="-39.2233"
                    x2="80"
                    y2="160"
                    gradientUnits="userSpaceOnUse">
                    <stop stopColor="#7E6DE9" />
                    <stop offset="1" stopColor="#381FD1" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <canvas
              id="starfield-canvas"
              className="absolute top-0 left-0 m-0 h-full w-full rounded-[40px] p-0 opacity-0 transition-opacity"
            />
            <Text
              id="worksText"
              level="mono"
              color="blue"
              className="mt-14 mb-8 text-center transition-opacity">
              Works in your design Tool and browser
            </Text>
          </div>
          <div
            className="flex flex-col items-center justify-between overflow-hidden bg-base-pink pr-8 pl-8"
            style={{
              gridArea: 'g',
              borderRadius: 40,
            }}>
            <Text level="mono" color="blue" className="mt-8 mb-8 text-center">
              In-App Education
            </Text>
            <img
              className="max-w-85.5"
              alt="Stark's Alt-Text feature highlighting that the supplied alt text is an example of keyword stuffing which actually hurts your SEO and is a bad experience for screen readers."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761248932/marketing/features/education_w2mtj2.png"
              style={{
                willChange: 'filter',
                filter: 'drop-shadow(0px 8px 60px rgba(0, 0, 0, 0.16))',
              }}
            />
          </div>
          <div
            className="flex flex-col items-center justify-between rounded-[40px] bg-base-purple-300 pr-8 pl-8"
            style={{ gridArea: 'h' }}>
            <img
              className="mt-auto max-w-90"
              alt="Stark's Alt-Text feature with numerous layers annotated showing examples of written, decorative and incorrect alt text."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761248924/marketing/features/alt-text__052023_kvismn.webp"
              style={{
                willChange: 'filter',
                filter: 'drop-shadow(0px 8px 60px rgba(0, 0, 0, 0.16))',
              }}
            />
            <Text level="mono" color="blue" className="mt-8 mb-8 text-center">
              Alt-Text Annotations
            </Text>
          </div>
          <div
            className="relative flex h-94 items-start justify-center overflow-hidden rounded-[40px] bg-base-yellow"
            style={{
              gridArea: 'i',
            }}>
            <Text level="mono" color="blue" className="mt-8 mb-8 text-center">
              Live preview in browser
            </Text>
            <img
              className="absolute top-18 right-0 w-181.25"
              alt="A plants website is shown with Stark's live preview feature allowing a user to directly change a color right in the browser."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761248979/marketing/features/live-preview_dgxg2y.webp"
              style={{
                willChange: 'filter',
                filter: 'drop-shadow(0px 8px 60px rgba(0, 0, 0, 0.16))',
              }}
            />
          </div>
          <div
            className="z-10 flex flex-col items-center justify-between rounded-[40px] bg-base-teal pr-8 pl-8"
            style={{
              gridArea: 'j',
            }}>
            <img
              className="mt-12 mb-auto max-w-90 rounded-lg"
              alt="Stark's Typography feature showing a layer with text size that is too small at 10px with the option to increase it."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761249009/marketing/features/typography__052023_lrpnvq.webp"
              style={{
                willChange: 'filter',
                filter: 'drop-shadow(0px 8px 60px rgba(0, 0, 0, 0.16))',
              }}
            />

            <Text level="mono" color="blue" className="mt-8 mb-8">
              Typography
            </Text>
          </div>
          <div
            className="z-10 flex flex-col items-center justify-end rounded-[40px] bg-base-yellow pr-8 pl-8"
            style={{ gridArea: 'k' }}>
            <img
              className="mt-8 max-w-76.75"
              alt="Three illustrated faces (a bald man with a moustache, a person with blond hair, and a woman with curly hair) are shown connected with lines in front of a cloud with the word SSO above them."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761249003/marketing/features/sso_izu9fj.png"
              style={{
                willChange: 'filter',
                filter: 'drop-shadow(0px 8px 60px rgba(0, 0, 0, 0.16))',
              }}
            />
            <Text level="mono" color="blue" className="mt-8 mb-8 text-center">
              SSO + Team Account Management
            </Text>
          </div>
          <div
            className="relative flex h-94 items-start justify-center overflow-hidden rounded-[40px] bg-base-purple-300 p-4"
            style={{
              gridArea: 'l',
            }}>
            <Text level="mono" color="blue" className="mt-10 text-center">
              Beautifully simple, lightning fast
            </Text>
            <img
              className="feature-grid-img-full absolute top-0 w-240 max-w-240"
              alt="A grid of all the different features offered within Stark, including Contrast, Alt-Text, Vision Simulator, and more."
              src="https://res.cloudinary.com/stark-lab/image/upload/v1761249005/marketing/features/suite__052023_dd7p9t.webp"
              style={{
                filter: 'drop-shadow(0px 8px 60px rgba(0, 0, 0, 0.16))',
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
